import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { es } from "date-fns/locale";
import api from "../../lib/api";
import { Plus, ArrowUpRight, CalendarCheck, Calendar as CalendarIcon, CheckCircle2, Clock } from "@/components/icons";
import { Calendar } from "../../components/ui/calendar";
import { useAuth } from "../../lib/auth";
import AppointmentsTable from "../../components/AppointmentsTable";
import { TableSkeleton } from "../../components/Skeletons";
import NewAppointmentModal from "../../components/appointment/NewAppointmentModal";
import AppleKpi from "../../components/AppleKpi";

export default function Agenda({ rolePath = "doctor" }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [appts, setAppts] = useState([]);
  const [patients, setPatients] = useState([]);
  const [payments, setPayments] = useState([]);
  const [firstLoad, setFirstLoad] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  const [day, setDay] = useState(() => {
    const fd = location.state?.focusDate;
    if (fd) {
      const [y, m, d] = fd.split("-").map(Number);
      return new Date(y, m - 1, d);
    }
    return new Date();
  });

  // Filtro de rango: null = día seleccionado, "week" = próximos 7 días, "all" = todas futuras
  const [rangeFilter, setRangeFilter] = useState(null);

  const role = user?.role === "secretary" ? "secretaria" : "doctor";

  const load = () => {
    Promise.all([
      api.get("/appointments").then((r) => setAppts(r.data)),
      api.get("/patients").then((r) => setPatients(r.data)),
      api.get("/payments").then((r) => setPayments(r.data)).catch(() => {}),
    ]).finally(() => setFirstLoad(false));
  };
  useEffect(() => {
    load();
  }, []);

  const dayKey = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, "0")}-${String(day.getDate()).padStart(2, "0")}`;
  const todayKey = (() => {
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
  })();
  const isToday = dayKey === todayKey;

  // Ocultar las citas COMPLETADAS de días anteriores a hoy — para que la
  // agenda de días pasados aparezca "en blanco" cuando todo se atendió.
  const visibleAppts = useMemo(
    () =>
      appts.filter((a) => {
        const k = a.date.slice(0, 10);
        if (k < todayKey && a.status === "completed") return false;
        return true;
      }),
    [appts, todayKey],
  );

  const tomorrowKey = (() => {
    const t = new Date();
    t.setDate(t.getDate() + 1);
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
  })();
  const isTomorrow = dayKey === tomorrowKey;

  const dayAppts = useMemo(() => {
    if (rangeFilter === "week") {
      // Próximos 7 días desde hoy (incluye hoy)
      const start = new Date();
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setDate(end.getDate() + 7);
      return visibleAppts
        .filter((a) => {
          const d = new Date(a.date);
          return d >= start && d < end;
        })
        .sort((a, b) => a.date.localeCompare(b.date));
    }
    if (rangeFilter === "all") {
      // Todas las citas desde hoy en adelante
      const start = new Date();
      start.setHours(0, 0, 0, 0);
      return visibleAppts
        .filter((a) => new Date(a.date) >= start)
        .sort((a, b) => a.date.localeCompare(b.date));
    }
    // Modo día individual
    return visibleAppts
      .filter((a) => a.date.slice(0, 10) === dayKey)
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [visibleAppts, dayKey, rangeFilter]);

  const patientById = Object.fromEntries(patients.map((p) => [p.id, p]));

  const paidAppointmentIds = useMemo(() => {
    const s = new Set();
    for (const p of payments) if (p.appointment_id) s.add(p.appointment_id);
    return s;
  }, [payments]);

  // Days that have at least one appointment (for visual highlight in calendar)
  const busyDays = visibleAppts.map((a) => {
    const [y, m, d] = a.date.slice(0, 10).split("-").map(Number);
    return new Date(y, m - 1, d);
  });

  // KPIs para el día seleccionado
  const stats = useMemo(() => {
    const done = dayAppts.filter((a) => a.status === "completed").length;
    const cancelled = dayAppts.filter((a) => a.status === "cancelled").length;
    const upcoming = dayAppts.filter((a) => new Date(a.date) > new Date()).length;
    return { total: dayAppts.length, done, cancelled, upcoming };
  }, [dayAppts]);

  // Buckets de la semana (próximos 7 días desde hoy)
  const weekBuckets = useMemo(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      const count = visibleAppts.filter((a) => a.date.slice(0, 10) === key).length;
      return {
        date: d,
        count,
        label: d.toLocaleDateString("es-ES", { weekday: "narrow" }),
        isToday: i === 0,
        isSelected: sameDay(d, day),
      };
    });
  }, [visibleAppts, day]);

  const weekAppts = weekBuckets.reduce((s, b) => s + b.count, 0);

  // Slots del día (horas ocupadas 07-21) para visualizar cargas
  const daySlots = useMemo(() => {
    const startHour = 7;
    const endHour = 21;
    const total = endHour - startHour;
    return Array.from({ length: total }, (_, i) => {
      const h = startHour + i;
      const hits = dayAppts.filter((a) => {
        const t = new Date(a.date);
        return t.getHours() === h;
      });
      return { hour: h, hits: hits.length, statuses: hits.map((x) => x.status) };
    });
  }, [dayAppts]);

  return (
    <div className="space-y-3 flex-1 flex flex-col min-h-0" data-testid="agenda-page">
      {/* ── KPI ROW ─────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <CitasGauge
          total={stats.total}
          done={stats.done}
          upcoming={stats.upcoming}
          isToday={isToday}
        />
        <CompletadasSpectrum
          done={stats.done}
          total={stats.total}
          cancelled={stats.cancelled}
        />
        <PorAtenderTimeline slots={daySlots} upcoming={stats.upcoming} />
        <SemanaBars buckets={weekBuckets} total={weekAppts} />
      </div>

      {/* ── MAIN GRID: Calendar + Appointments ──────────────────────── */}
      <div className="grid grid-cols-12 gap-3 flex-1 min-h-0">
        {/* Calendar */}
        <div
          className="col-span-12 xl:col-span-4 bg-white rounded-xl border border-black/[0.05] p-4 flex flex-col min-h-0"
          data-testid="agenda-calendar"
        >
          <div className="flex items-center justify-between mb-1">
            <div>
              <div className="text-[13px] font-semibold text-black">Calendario</div>
              <div className="text-[11px] text-black/45">Selecciona un día</div>
            </div>
            <span className="w-7 h-7 rounded-md bg-[#F2F3F5] flex items-center justify-center flex-shrink-0">
          <ArrowUpRight className="w-3.5 h-3.5 text-black/70" strokeWidth={1.75} weight="regular" />
        </span>
          </div>

          <div className="flex-1 min-h-0 flex items-center justify-center">
            <Calendar
              mode="single"
              locale={es}
              selected={day}
              month={day}
              onMonthChange={(m) => setDay(m)}
              onSelect={(d) => { if (d) { setDay(d); setRangeFilter(null); } }}
              numberOfMonths={1}
              modifiers={{ busy: busyDays }}
              modifiersClassNames={{
                busy:
                  "relative after:content-[''] after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:w-1 after:h-1 after:rounded-full after:bg-black/60",
              }}
              className="agenda-calendar-mini"
              classNames={{
                months: "flex flex-col gap-4",
                month: "flex-1 min-w-0 space-y-3",
                caption:
                  "flex justify-center pt-1 relative items-center pb-2",
                caption_label:
                  "text-[13px] font-semibold text-black capitalize",
                nav: "space-x-1 flex items-center",
                nav_button:
                  "h-7 w-7 rounded-lg bg-white border border-black/10 hover:bg-black/[0.03] text-black inline-flex items-center justify-center",
                nav_button_previous: "absolute left-1",
                nav_button_next: "absolute right-1",
                table: "w-full border-collapse",
                head_row: "flex w-full",
                head_cell:
                  "text-black/40 font-semibold w-9 h-8 text-[10px] uppercase tracking-wider flex items-center justify-center",
                row: "flex w-full mt-1",
                cell: "w-9 h-9 text-center text-[13px] p-0 relative",
                day:
                  "h-8 w-8 mx-auto p-0 font-medium rounded-lg hover:bg-black/[0.05] transition flex items-center justify-center text-black cursor-pointer",
                day_selected:
                  "!bg-black !text-white !font-semibold hover:!bg-black focus:!bg-black",
                day_today:
                  "ring-1 ring-black/50 ring-offset-1 ring-offset-white font-semibold text-black",
                day_outside: "text-black/25 hover:bg-transparent",
                day_disabled: "text-black/25 opacity-50",
                day_hidden: "invisible",
              }}
            />
          </div>
        </div>

        {/* Appointments list */}
        <div
          className="col-span-12 xl:col-span-8 bg-white rounded-xl border border-black/[0.05] p-4 flex flex-col min-h-0"
          data-testid="agenda-day-list"
        >
          <div className="flex flex-col mb-3 gap-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="text-[13px] font-semibold text-black flex items-center gap-1.5">
                  <CalendarCheck className="w-4 h-4 text-black/70" strokeWidth={1.75} weight="regular" />
                  {rangeFilter === "week"
                    ? "Próximos 7 días"
                    : rangeFilter === "all"
                    ? "Todas las citas"
                    : "Citas del día"}
                </div>
                <div className="text-[11px] text-black/45 mt-0.5 capitalize">
                  {rangeFilter === "week"
                    ? "Vista semanal desde hoy"
                    : rangeFilter === "all"
                    ? "Todas las citas desde hoy"
                    : day.toLocaleDateString("es-ES", {
                        weekday: "long",
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      })}
                  {" · "}
                  <span className="text-black/60 font-semibold">
                    {dayAppts.length} {dayAppts.length === 1 ? "cita" : "citas"}
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setModalOpen(true)}
                data-testid="new-appointment-button"
                className="h-9 pl-2.5 pr-3 rounded-lg bg-[#0071E3] hover:bg-[#0077ED] text-white text-[13px] font-semibold inline-flex items-center gap-1.5 transition flex-shrink-0"
              >
                <Plus className="w-4 h-4" strokeWidth={2} weight="regular" /> Nueva cita
              </button>
            </div>

            {/* Filtros rápidos */}
            <div className="flex items-center gap-1.5 flex-wrap">
              {[
                { k: "today", label: "Hoy" },
                { k: "tomorrow", label: "Mañana" },
                { k: "week", label: "Esta semana" },
                { k: "all", label: "Todas" },
              ].map((chip) => {
                const active =
                  (chip.k === "today" && rangeFilter === null && isToday) ||
                  (chip.k === "tomorrow" && rangeFilter === null && isTomorrow) ||
                  (chip.k === "week" && rangeFilter === "week") ||
                  (chip.k === "all" && rangeFilter === "all");
                return (
                  <button
                    key={chip.k}
                    type="button"
                    data-testid={`agenda-chip-${chip.k}`}
                    onClick={() => {
                      if (chip.k === "today") {
                        setDay(new Date());
                        setRangeFilter(null);
                      } else if (chip.k === "tomorrow") {
                        const t = new Date();
                        t.setDate(t.getDate() + 1);
                        setDay(t);
                        setRangeFilter(null);
                      } else if (chip.k === "week") {
                        setRangeFilter("week");
                      } else if (chip.k === "all") {
                        setRangeFilter("all");
                      }
                    }}
                    className={`h-8 px-3 rounded-full text-[12px] font-semibold transition ${
                      active
                        ? "bg-[#0071E3] text-white"
                        : "bg-white border border-black/[0.08] text-black hover:bg-black/[0.03]"
                    }`}
                  >
                    {chip.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto pr-1 no-scrollbar">
            {firstLoad ? (
              <TableSkeleton rows={6} />
            ) : (
              <AppointmentsTable
                appointments={dayAppts}
                patientById={patientById}
                showIniciar={isToday && user?.role === "doctor"}
                paidAppointmentIds={paidAppointmentIds}
                onChanged={load}
                testid="agenda-appts-table"
                emptyText="Sin citas para este día."
              />
            )}
          </div>
        </div>
      </div>

      <NewAppointmentModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreated={(dateStr) => {
          load();
          if (dateStr) {
            const [y, m, d] = dateStr.slice(0, 10).split("-").map(Number);
            setDay(new Date(y, m - 1, d));
          }
        }}
        prefillDate={(() => {
          // Prefill con el día seleccionado a las 09:00
          const hh = "09:00";
          return `${dayKey}T${hh}`;
        })()}
      />
    </div>
  );
}

// ─── KPI Widgets ──────────────────────────────────────────────────────────

function sameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function CitasGauge({ total, done, upcoming, isToday }) {
  const pending = Math.max(0, total - done);
  return (
    <AppleKpi
      testid="agenda-kpi-total"
      icon={CalendarIcon}
      title="Citas del día"
      description={isToday ? "Panorama completo de las citas que tienes hoy." : "Resumen de las citas para el día seleccionado."}
      label="Agendadas"
      value={total}
      footer={
        total === 0
          ? "Sin citas en este día"
          : `${done} completadas · ${pending} pendientes`
      }
    />
  );
}

function CompletadasSpectrum({ done, total, cancelled }) {
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <AppleKpi
      testid="agenda-kpi-completed"
      icon={CheckCircle2}
      title="Completadas"
      description="Progreso del día — consultas atendidas y canceladas."
      label="Atendidas"
      value={`${done}/${total}`}
      footer={
        total === 0
          ? "Aún sin consultas"
          : `${pct}% completado${cancelled > 0 ? ` · ${cancelled} canceladas` : ""}`
      }
    />
  );
}

function PorAtenderTimeline({ slots, upcoming }) {
  return (
    <AppleKpi
      testid="agenda-kpi-upcoming"
      icon={Clock}
      title="Por atender"
      description="Citas que aún faltan en lo que resta del día."
      label="Restantes"
      value={upcoming}
      footer={upcoming === 0 ? "Día cerrado" : `${upcoming === 1 ? "1 cita" : `${upcoming} citas`} por delante`}
    />
  );
}

function SemanaBars({ buckets, total }) {
  const busy = buckets.filter((b) => b.count > 0).length;
  return (
    <AppleKpi
      testid="agenda-kpi-week"
      icon={CalendarCheck}
      title="Próximos 7 días"
      description="Vista rápida de la carga semanal que viene."
      label="Agendadas"
      value={total}
      footer={`${busy} de 7 días con citas`}
    />
  );
}
